import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Badge } from '../components/ui';

export default function AdminWorkspaces() {
  const { isSuperAdmin } = useAuth();
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin || !tenantId) return;
    const fetchWorkspaces = async () => {
      try {
        const res = await api.get(`/admin/tenants/${tenantId}/workspaces`);
        setTenant(res.data.tenant);
        setWorkspaces(res.data.workspaces);
      } catch (err) {
        console.error('Erreur chargement workspaces:', err);
        setError('Erreur lors du chargement des clients');
      } finally {
        setLoading(false);
      }
    };
    fetchWorkspaces();
  }, [isSuperAdmin, tenantId]);

  const toggleFeature = async (workspace, feature, label) => {
    const current = !!workspace?.entitlements?.[feature];
    const newVal = !current;
    const key = `${feature}:${workspace._id}`;
    setUpdatingKey(key);
    setError(null);
    try {
      const res = await api.patch(`/admin/tenants/${tenantId}/workspaces/${workspace._id}/entitlements`, { [feature]: newVal });
      const updated = res.data;
      setWorkspaces((prev) => prev.map((w) => (w._id === updated._id ? { ...w, entitlements: updated.entitlements } : w)));
    } catch (err) {
      console.error(`Erreur update ${feature}:`, err);
      setError(`Impossible de mettre à jour ${label}`);
    } finally {
      setUpdatingKey(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white p-6 rounded shadow">
        <h1 className="text-xl font-semibold mb-2">Accès refusé</h1>
        <p className="text-gray-600">Section réservée au super administrateur.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="bg-white p-6 rounded shadow"><p className="text-gray-600">Chargement...</p></div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="secondary" size="sm" onClick={() => navigate('/admin/tenants')}>
          &larr; Retour
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">{tenant?.name || 'Cabinet'}</h1>
          <p className="text-sm text-gray-500">Gestion des modules par client</p>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-100 text-red-800 text-sm">{error}</div>}

      <div className="bg-white shadow rounded overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Client</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Matricule</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Statut</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Comptabilité</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Stock</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Sign. Élec.</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Gestion Cabinet</th>
            </tr>
          </thead>
          <tbody>
            {workspaces.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-4 text-center text-gray-500">
                  Aucun dossier client pour ce cabinet.
                </td>
              </tr>
            )}
            {workspaces.map((ws) => {
              const isCabinetOwn = ws.type === 'CABINET_OWN';
              const acctEnabled = !!ws.entitlements?.accountingEnabled;
              const stockEnabled = !!ws.entitlements?.stockEnabled;
              const esigEnabled = !!ws.entitlements?.eSignatureEnabled;
              const cabEnabled = !!ws.entitlements?.cabinetManagementEnabled;
              const acctKey = `accountingEnabled:${ws._id}`;
              const stockKey = `stockEnabled:${ws._id}`;
              const esigKey = `eSignatureEnabled:${ws._id}`;
              const cabKey = `cabinetManagementEnabled:${ws._id}`;

              return (
                <tr key={ws._id} className={`border-t border-gray-100 hover:bg-gray-50 transition-colors ${isCabinetOwn ? 'bg-gray-50' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">
                      {ws.clientName || ws.name}
                    </div>
                    {isCabinetOwn && (
                      <span className="text-xs text-gray-400 italic">Dossier interne du cabinet</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{ws.clientTaxId || '-'}</td>
                  <td className="px-4 py-2">
                    <Badge variant={isCabinetOwn ? 'info' : 'gray'}>
                      {isCabinetOwn ? 'Interne' : 'Client'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={ws.status === 'active' ? 'success' : 'warning'}>
                      {ws.status === 'active' ? 'Actif' : 'Archivé'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={acctEnabled ? 'success' : 'gray'}>{acctEnabled ? 'Activée' : 'Désactivée'}</Badge>
                      <Button variant={acctEnabled ? 'warning' : 'success'} size="sm"
                        onClick={() => toggleFeature(ws, 'accountingEnabled', 'Comptabilité')}
                        disabled={updatingKey === acctKey}>
                        {updatingKey === acctKey ? '...' : acctEnabled ? 'Désactiver' : 'Activer'}
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={stockEnabled ? 'success' : 'gray'}>{stockEnabled ? 'Activé' : 'Désactivé'}</Badge>
                      <Button variant={stockEnabled ? 'warning' : 'success'} size="sm"
                        onClick={() => toggleFeature(ws, 'stockEnabled', 'Stock')}
                        disabled={updatingKey === stockKey}>
                        {updatingKey === stockKey ? '...' : stockEnabled ? 'Désactiver' : 'Activer'}
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={esigEnabled ? 'success' : 'gray'}>{esigEnabled ? 'Activée' : 'Désactivée'}</Badge>
                      <Button variant={esigEnabled ? 'warning' : 'success'} size="sm"
                        onClick={() => toggleFeature(ws, 'eSignatureEnabled', 'Signature électronique')}
                        disabled={updatingKey === esigKey}>
                        {updatingKey === esigKey ? '...' : esigEnabled ? 'Désactiver' : 'Activer'}
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={cabEnabled ? 'success' : 'gray'}>{cabEnabled ? 'Activée' : 'Désactivée'}</Badge>
                      <Button variant={cabEnabled ? 'warning' : 'success'} size="sm"
                        onClick={() => toggleFeature(ws, 'cabinetManagementEnabled', 'Gestion Cabinet')}
                        disabled={updatingKey === cabKey}>
                        {updatingKey === cabKey ? '...' : cabEnabled ? 'Désactiver' : 'Activer'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Chaque dossier client peut avoir ses propres modules activés ou désactivés indépendamment.
      </div>
    </div>
  );
}
